/******************************************************************************
 * Droits d'auteur (c) 2024 Niralakka
 *
 * La permission est accordée, gratuitement, à toute personne obtenant une copie de ce logiciel
 * et des fichiers de documentation associés (le "Logiciel"), de traiter le Logiciel sans restriction,
 * y compris, sans limitation, les droits d'utiliser, de copier, de modifier, de fusionner, de publier, de distribuer, de concéder
 * une sous-licence, et/ou de vendre des copies du Logiciel, et de permettre aux personnes à qui le Logiciel est fourni de le faire,
 * sous réserve des conditions suivantes :
 *
 * L'avis de droit d'auteur ci-dessus et cet avis d'autorisation doivent être inclus dans toutes les copies ou parties substantielles
 * du Logiciel.
 *
 * LE LOGICIEL EST FOURNI "TEL QUEL", SANS GARANTIE D'AUCUNE SORTE, EXPLICITE OU IMPLICITE,
 * Y COMPRIS, MAIS SANS S'Y LIMITER, LES GARANTIES DE QUALITÉ MARCHANDE, D'ADÉQUATION À UN USAGE PARTICULIER ET D'ABSENCE DE CONTREFAÇON.
 * EN AUCUN CAS LES AUTEURS OU LES TITULAIRES DES DROITS D'AUTEUR NE SERONT RESPONSABLES DE TOUTE RÉCLAMATION, DOMMAGES OU AUTRE RESPONSABILITÉ,
 * QUE CE SOIT DANS UNE ACTION DE CONTRAT, DE DÉLIT OU AUTRE, DÉCOULANT DE, HORS DE OU EN LIEN AVEC LE LOGICIEL OU L'UTILISATION
 * OU D'AUTRES TRANSACTIONS DANS LE LOGICIEL.
 *
 ******************************************************************************/

// Version: 1.0.0

import axios from 'axios';
import { NrlkException } from "./exception.js";

// Utilisation des variables globales de window
const API_URL = window.API_URL || "https://api-dev.niralakka.fr";

/**
 * Classe API pour gérer les requêtes HTTP vers l'API Niralakka
 *
 * @version 1.0.0
 *
 * @param {string} entreprise
 * @param {string} token
 *
 * @method setEntreprise(entreprise) Ajouter au Header de la requête l'entreprise
 * @method setToken(token) Ajouter au Header de la requête le token
 * @method sendRequest(method, endpoint, data) Fonction pour envoyer une requête HTTP
 * @method get(endpoint) Fonction pour envoyer une requête GET
 * @method post(endpoint, data) Fonction pour envoyer une requête POST
 * @method put(endpoint, data) Fonction pour envoyer une requête PUT
 * @method delete(endpoint) Fonction pour envoyer une requête DELETE
 *
 * @throws {NrlkException}
 *
 * @returns {NrlkAPI} API
 */
export class NrlkAPI {
  /**
   * Constructeur de la classe API pour gérer les requêtes HTTP vers l'API Niralakka
   *
   * @param {string} entreprise
   * @param {string} token
   *
   * @returns {NrlkAPI}
   *
   * @constructor
   */
  constructor(entreprise = "", token = null) {

    // Selon l'environnement, on change l'URL de base
    this.baseUrl = window.API_URL || API_URL; // URL de base de l'API
    // this.entreprise = entreprise = "preprod"; // Entreprise de preprod
    this.headers = {
      "Content-Type": "application/json",
      "Accept": "application/json",
    };
    this.setEntreprise(entreprise);
    this.setToken(token);
  }

  /**
   * Set Entreprise
   *
   * Ajouter au Header de la requête l'entreprise
   *
   * @param {string} entreprise
   *
   * @returns {this}
   */
  async setEntreprise(entreprise) {
    this.headers["Entreprise"] = entreprise;
  }

  /**
   * Set Token.
   *
   * Ajouter au Header de la requête le token
   *
   * @param {string} token
   *
   * @returns {this}
   */
  async setToken(token) {
    this.headers["Token"] = token;
  }

  /**
   * Get Entreprise
   *
   * Récupérer l'entreprise
   *
   * @returns {string} Entreprise
   */
  async getEntreprise() {
    return this.headers["Entreprise"];
  }

  /**
   * Get Token
   *
   * Récupérer le token
   *
   * @returns {string} Token
   */
  async getToken() {
    return this.headers["Token"];
  }

  // Fonction pour envoyer une requête HTTP
  /**
   *
   * @param {string} method
   * @param {string} endpoint
   * @param {JSON} data
   *
   * @returns {response}
   *
   * @throws {NrlkException}
   */
  async sendRequest(method, endpoint, data = {}) {
    try {
      // Vérification si c'est un FormData pour un envoi multipart
      if (data instanceof FormData) {
        // Axios s'occupe du bon Content-Type pour les FormData
        delete this.headers["Content-Type"]; // On laisse Axios gérer ce header
        this.headers["Content-Type"] = "multipart/form-data";
      }
      // Vérification de la méthode pour envoyer les données
      if (method === "GET" || method === "DELETE") {
        data = { params: data };
      } else {
        data = { data: data };
      }
      // console.log("method", method)
      // console.log("headers", this.headers)
      // console.log("url", this.baseUrl + endpoint)
      // Utilisation de axios pour envoyer la requête
      const response = await axios({
        method: method,
        url: this.baseUrl + endpoint,
        headers: this.headers,
        withCredentials: true,
        ...data,
      });
      // Plus tard, on pourra ajouter des vérifications sur le code de retour
      // Vérification de la réponse
      /*if(response.data.result != 1) {
                throw new NrlkException(response.data.message);
      }*/
      // Retourne la réponse
      return response.data;
    } catch (error) {
      // console.log(error)
      throw new NrlkException(
        error + " - " + (error.response?.data?.message || "Erreur inconnue")
      );
    }
  }

  /**
   * Fonction pour envoyer une requête GET
   *
   * @param {string} endpoint
   *
   * @returns {Promise} Données
   *
   * @throws {NrlkException}
   *
   **/
  async get(endpoint, params = {}) {
    return await this.sendRequest("GET", endpoint, params);
  }

  /**
   * Fonction pour envoyer une requête POST
   *
   * @param {string} endpoint
   * @param {object} data
   *
   * @returns {Promise} Données
   *
   * @throws {NrlkException}
   *
   **/
  async post(endpoint, data) {
    return await this.sendRequest("POST", endpoint, data);
  }

  /**
   * Fonction pour envoyer une requête PUT
   *
   * @param {string} endpoint
   * @param {object} data
   *
   * @returns {Promise} Boolean
   *
   * @throws {NrlkException}
   *
   **/
  async put(endpoint, data) {
    return await this.sendRequest("PUT", endpoint, data);
  }

  /**
   * Fonction pour envoyer une requête DELETE
   *
   * @param {string} endpoint
   *
   * @returns {Promise} Boolean
   *
   * @throws {NrlkException}
   *
   **/
  async delete(endpoint) {
    return await this.sendRequest("DELETE", endpoint);
  }
}












